/**
 * ActivityChart — 14-day activity bar chart (pure CSS, no chart library).
 */

import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useApi } from '@/hooks/use-api'

type DayData = {
  readonly date: string
  readonly count: number
}

type ActivityChartResponse = {
  readonly days: readonly DayData[]
}

function getDayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)
}

export function ActivityChart() {
  const apiFetch = useApi()

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'activity-chart'],
    queryFn: async (): Promise<ActivityChartResponse> => {
      const res = await apiFetch<ActivityChartResponse>('/api/leads/analytics/activity-chart')
      if (!res.success || !res.data) return { days: [] }
      return res.data
    },
    staleTime: 60_000,
  })

  const days = data?.days ?? []
  const maxCount = Math.max(1, ...days.map((d) => d.count))
  const totalActions = days.reduce((sum, d) => sum + d.count, 0)

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold">Activity (14 days)</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-32 w-full" /></CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Activity (14 days)</CardTitle>
          <span className="text-xs text-muted-foreground">{totalActions} total actions</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-[3px] h-24">
          {days.map((day) => {
            const heightPct = maxCount > 0 ? (day.count / maxCount) * 100 : 0
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${day.date}: ${day.count} actions`}>
                <span className="text-[9px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity tabular-nums">
                  {day.count}
                </span>
                <div
                  className="w-full rounded-t-sm bg-primary/70 hover:bg-primary transition-colors min-h-[2px]"
                  style={{ height: `${Math.max(2, heightPct)}%` }}
                />
              </div>
            )
          })}
        </div>
        <div className="flex gap-[3px] mt-1">
          {days.map((day, i) => (
            <div key={day.date} className="flex-1 text-center">
              {i % 2 === 0 && <span className="text-[9px] text-muted-foreground">{getDayLabel(day.date)}</span>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
